/**
 * CitrusQuizIcons - small inline SVG marks for the USCN quiz UX v2 features:
 * the reading-counter citrus mark, build-up growth stages, and celebration
 * drift leaf.
 *
 * Kept separate from the DMM icon set so the two quizzes stay fully decoupled.
 */
import React from 'react';

interface SvgProps {
  className?: string | undefined;
  size?: number | undefined;
}

/* Small leaf lens shape drawn from two mirrored arcs. */
const leafPath = (x: number, y: number, s: number, flip = false): string => {
  const d = flip ? -s : s;
  return `M${x} ${y}c${-d * 0.9} ${-s * 0.15} ${-d * 1.45} ${-s * 0.75} ${-d * 1.6} ${-s * 1.6}c${d * 0.9} ${s * 0.15} ${d * 1.45} ${s * 0.75} ${d * 1.6} ${s * 1.6}z`;
};

/** UX-5 counter chip mark: a small citrus fruit with a leaf. */
export const CitrusFruitMark: React.FC<SvgProps> = ({ className, size = 16 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="14" r="6.5" />
    <path d="M12 7.5V5.5" />
    <path d={leafPath(12, 5.5, 2.6)} />
  </svg>
);

/**
 * UX-6 build-up visual: a citrus tree growing stage by stage.
 * 1 the seed. 2 the seed cracks, a root. 3 a sprout. 4 leaves.
 * 5 the small tree complete, with fruit.
 */
export const CitrusGrowthStage: React.FC<{ stage: number } & SvgProps> = ({
  stage,
  className,
  size = 72,
}) => {
  const clamped = Math.max(1, Math.min(5, stage));
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.5 16.5h15" />
      {clamped === 1 && <ellipse cx="12" cy="13.2" rx="2.4" ry="3" />}
      {clamped === 2 && (
        <>
          <ellipse cx="12" cy="13.2" rx="2.4" ry="3" />
          <path d="M12 11.2v2.6" />
          <path d="M12 16.5v2.4m0 0l-1.8 1.6m1.8-1.6l1.8 1.6" />
        </>
      )}
      {clamped >= 3 && <path d="M12 16.5v2.4m0 0l-1.8 1.6m1.8-1.6l1.8 1.6" />}
      {clamped === 3 && (
        <>
          <path d="M12 16.5V11" />
          <path d={leafPath(12, 11.4, 2.2)} />
          <path d={leafPath(12, 11.4, 2.2, true)} />
        </>
      )}
      {clamped === 4 && (
        <>
          <path d="M12 16.5V8.5" />
          <path d={leafPath(12, 13.2, 2.2)} />
          <path d={leafPath(12, 13.2, 2.2, true)} />
          <path d={leafPath(12, 9.2, 2)} />
          <path d={leafPath(12, 9.2, 2, true)} />
        </>
      )}
      {clamped === 5 && (
        <>
          <path d="M12 16.5v-6" />
          <circle cx="12" cy="7.4" r="3.6" />
          <circle cx="13.4" cy="8.6" r="1" />
        </>
      )}
    </svg>
  );
};

/** UX-7 celebration: a single drifting leaf (positioned and animated by CSS). */
export const CitrusDriftLeaf: React.FC<SvgProps> = ({ className, size = 18 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 18.5C6 10.5 11 5.5 19 5c.5 8-4.5 13-12.5 13.5z" />
    <path d="M6 18.5c2.2-4.6 5.4-8 9.6-10.2" />
  </svg>
);
